/**
 * Talking to somebody who works here, from the tab.
 *
 * The community chat answers "is anybody else stuck on this", and for two
 * years that was the only conversation the product had. It is the wrong shape
 * for the other question a learner asks — "my card was charged twice", "the
 * virtual machine will not boot on this laptop", "I bought this and the paid
 * levels are still locked" — because the answer to that one is not in the
 * room. It is held by whoever runs the deployment, and a learner asking it in
 * Room 7 is asking ninety strangers to relay it.
 *
 * So there is a second kind of conversation, and it is deliberately the same
 * machinery: a support thread **is a chat room**, one of the `support-…`
 * family (`@engineer-os/chat`), with a door on it that admits the learner it
 * belongs to and the people on the deployment's support roster and nobody
 * else. Everything the panel already knows how to do — history, editing,
 * pictures, reporting, the reconnect loop — works in it unchanged, and
 * nothing in `useChat` had to learn that support exists.
 *
 * This module is the small part that is *not* a room: the three routes that
 * open a thread, say which one you have, and close it
 * (`worker/support.ts`). The console the other side of the conversation sits
 * at is `lib/desk.ts`, which shares no code with this on purpose — a learner's
 * bundle should not carry the queue.
 *
 * ## Two rules, both borrowed
 *
 * 1. **Nothing here may break the chat.** Every call collapses to an empty or
 *    neutral answer rather than throwing, exactly as `lib/inbox.ts` does and
 *    for the same reason: a Worker that has never heard of `/api/support`, a
 *    session that expired an hour ago, a deployment with nobody on the roster
 *    and no network are four different facts and one interface. The panel
 *    draws what it can and offers what it cannot.
 * 2. **Signed out is an empty snapshot, not an error.** The route answers 404
 *    without a session, because an answer that differs between a stranger and
 *    a signed-out learner is a way to ask whether an account exists
 *    (`worker/chat-store.ts`, and `docs/teams.md` before it). Here that 404
 *    reads as `enabled: false`, which is what the panel wants anyway.
 */
import type { SupportTopic } from '@engineer-os/chat';
import { chatUrl } from './chat';
import { getLocale } from './locale';

/** Whether the last message in a thread came from the learner or from the desk. */
export type SupportFrom = 'learner' | 'staff';

/** A thread is open until somebody closes it, and reopens if the learner writes again. */
export type SupportState = 'open' | 'resolved';

/**
 * One conversation, as the learner is told about it.
 *
 * `room` is carried rather than derived, although `supportRoom(id)` would
 * give the same string. The room id is the server's to decide: if the family
 * ever gains a prefix or a shard, a tab that built the id itself would be
 * opening a socket to a room nobody is in, and it would do it silently.
 */
export interface SupportThread {
  id: string;
  /** The chat room this conversation happens in. Hand it to `chat.join`. */
  room: string;
  topic: SupportTopic;
  subject: string | null;
  state: SupportState;
  opened: number;
  lastTs: number;
  lastFrom: SupportFrom;
  /**
   * The name the agent holding this thread appears under, or null while
   * nobody has picked it up. It is the roster's display name and not their
   * account name — see `worker/index.ts`, where the substitution happens.
   */
  agent: string | null;
}

/** Everything the panel needs in one request. Empty is a valid answer. */
export interface SupportSnapshot {
  /**
   * Whether this deployment can take a message at all.
   *
   * False for four different reasons and the panel treats them alike: the
   * feature is switched off, accounts are not configured, the reader is
   * signed out, or **the roster is empty**. That last one is the reason this
   * is not simply "is the feature on": a door into a room nobody staffs is
   * worse than a closed door, because the learner waits.
   */
  enabled: boolean;
  /** How many agents have read the queue recently enough to be here now. */
  staffOnline: number;
  /** The conversation they have going, or null. At most one is ever open. */
  thread: SupportThread | null;
  /** The last few they have closed, newest first, so a transcript is findable. */
  recent: SupportThread[];
}

/** What a tab with nothing to go on shows: no door, no threads, nobody about. */
export const NO_SUPPORT: SupportSnapshot = { enabled: false, staffOnline: 0, thread: null, recent: [] };

/**
 * `credentials: 'include'` rather than `'same-origin'`, and `no-store`.
 *
 * The same pair `lib/inbox.ts` uses, for the same reason: a static build can
 * be pointed at a Worker on another origin with `VITE_CHAT_ORIGIN`, and every
 * answer here is about one signed-in person and must never sit in a cache
 * that somebody else can read.
 */
const SESSION: RequestInit = { credentials: 'include', cache: 'no-store' };

/* ── Reading what the Worker said ───────────────────────────────────────── */

/**
 * The shapes below re-check every field rather than trusting the parse.
 *
 * Not because the Worker is suspect, but because an old deploy is: this tab
 * may be talking to a version of the Worker that predates a field, and the
 * failure mode of trusting it is a `NaN` in a timestamp or an `undefined`
 * where a string belongs, surfacing three components away from the fetch that
 * caused it. `lib/projects.ts` and `lib/inbox.ts` both read their answers this
 * way and this is the third copy of the same judgement.
 */
const TOPICS: readonly string[] = ['access', 'billing', 'tasks', 'content', 'bug', 'other'];

function readThread(raw: unknown): SupportThread | null {
  if (!raw || typeof raw !== 'object') return null;
  const d = raw as Record<string, unknown>;
  if (typeof d.id !== 'string' || typeof d.room !== 'string') return null;
  if (typeof d.topic !== 'string' || !TOPICS.includes(d.topic)) return null;
  const state = d.state === 'resolved' ? 'resolved' : 'open';
  const lastFrom = d.lastFrom === 'staff' ? 'staff' : 'learner';
  return {
    id: d.id,
    room: d.room,
    topic: d.topic as SupportTopic,
    subject: typeof d.subject === 'string' && d.subject !== '' ? d.subject : null,
    state,
    opened: typeof d.opened === 'number' ? d.opened : 0,
    lastTs: typeof d.lastTs === 'number' ? d.lastTs : 0,
    lastFrom,
    agent: typeof d.agent === 'string' && d.agent !== '' ? d.agent : null,
  };
}

function readSnapshot(raw: unknown): SupportSnapshot {
  if (!raw || typeof raw !== 'object') return NO_SUPPORT;
  const d = raw as Record<string, unknown>;
  const recent = Array.isArray(d.recent)
    ? d.recent.map(readThread).filter((t): t is SupportThread => t !== null)
    : [];
  return {
    enabled: d.enabled === true,
    staffOnline: typeof d.staffOnline === 'number' && d.staffOnline > 0 ? Math.floor(d.staffOnline) : 0,
    thread: readThread(d.thread),
    recent,
  };
}

/* ── The three calls ────────────────────────────────────────────────────── */

/**
 * What the reader has going with support, or nothing at all.
 *
 * Takes a signal because the caller polls it and an account can change under
 * an in-flight request; `useChatInbox` does the same with a generation counter
 * beside it, and the two together are what stop one person's threads arriving
 * after somebody else has signed in.
 */
export async function fetchSupport(signal?: AbortSignal): Promise<SupportSnapshot> {
  try {
    const r = await fetch(chatUrl('/api/support').href, { ...SESSION, signal });
    if (!r.ok) return NO_SUPPORT;
    return readSnapshot(await r.json());
  } catch {
    return NO_SUPPORT;
  }
}

/**
 * What opening a thread can tell the panel apart.
 *
 * `busy` is the day's allowance, `closed` is a deployment that cannot take the
 * message, and `other` is everything else — a network that is not there, a
 * Worker that is not deployed, a session that expired between the page loading
 * and the button being pressed. Three outcomes and not one boolean, because
 * the sentence the panel says differs for each and "something went wrong" is
 * the sentence that makes somebody give up.
 */
export type OpenOutcome =
  | { ok: true; thread: SupportThread }
  | { ok: false; reason: 'busy' | 'closed' | 'other' };

/**
 * Start a conversation, or hand back the one already going.
 *
 * Idempotent on purpose: the route answers 200 with the existing thread rather
 * than refusing, so a double-tapped button on a slow connection is one
 * conversation and not two. A learner has at most one open thread and the
 * database enforces it (`support_threads_one_open`), which means the failure
 * this shape is avoiding is not hypothetical — without the 200 it would be a
 * constraint violation surfacing as `other`.
 */
export async function openSupport(topic: SupportTopic, subject?: string): Promise<OpenOutcome> {
  try {
    /**
     * `?lang=` says which language to answer in, and it has to be said here.
     *
     * The Worker's fallback is `Accept-Language`, which is the browser's
     * setting rather than the product's: an Arabic reader who switched the
     * language in the corner of this page is very often on a browser
     * installed in English, and without this line their conversation arrives
     * at the desk labelled `en` and gets the English saved answer. The tab is
     * the only thing that knows which of the two they are actually reading,
     * so the tab is what has to say it.
     *
     * A query parameter rather than a field in the body, because `?lang=` is
     * already the convention this Worker reads it by (`entitlement.ts`).
     */
    const url = chatUrl('/api/support');
    url.searchParams.set('lang', getLocale());
    const r = await fetch(url.href, {
      ...SESSION,
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(subject ? { topic, subject } : { topic }),
    });
    if (r.status === 429) return { ok: false, reason: 'busy' };
    if (r.status === 404 || r.status === 503) return { ok: false, reason: 'closed' };
    if (!r.ok) return { ok: false, reason: 'other' };
    const thread = readThread((await r.json() as { thread?: unknown }).thread);
    return thread ? { ok: true, thread } : { ok: false, reason: 'other' };
  } catch {
    return { ok: false, reason: 'other' };
  }
}

/**
 * Close the conversation from this end.
 *
 * Nothing is deleted and the room stays readable; what changes is that the
 * desk stops counting it as waiting. Writing in it again reopens it, which is
 * why this is safe to offer to a learner who may have closed it too early —
 * the mistake costs one more message.
 */
export async function resolveSupport(): Promise<boolean> {
  try {
    const r = await fetch(chatUrl('/api/support/resolve').href, {
      ...SESSION,
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: '{}',
    });
    return r.ok;
  } catch {
    return false;
  }
}
